import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CameraIcon, UploadSimpleIcon, IdentificationCardIcon, UserCircleIcon,
  CheckCircleIcon, WarningIcon, ArrowsClockwiseIcon, MagicWandIcon,
  PlusIcon, TrashIcon, XIcon,
} from "@phosphor-icons/react";

function CameraCapture({ open, onClose, onCapture, title }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facingMode } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => setReady(true);
        }
      } catch (e) {
        toast.error("Camera unavailable. Please use Upload instead.");
        onClose();
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      setReady(false);
    };
  }, [open, facingMode]); // eslint-disable-line

  const snap = () => {
    const v = videoRef.current;
    if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    onCapture(dataUrl);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="relative rounded-md overflow-hidden border border-border bg-black aspect-video">
          <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
          {!ready && <div className="absolute inset-0 flex items-center justify-center text-white text-sm">Starting camera…</div>}
        </div>
        <div className="flex justify-between items-center mt-3">
          <Button variant="outline" size="sm" onClick={() => setFacingMode((m) => m === "environment" ? "user" : "environment")}>
            <ArrowsClockwiseIcon size={14} className="mr-1" /> Flip
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={snap} disabled={!ready} data-testid="kyc-camera-capture">
              <CameraIcon size={14} className="mr-1" /> Capture
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KycSlot({ label, image, onCapture, testId, icon: Icon }) {
  const fileRef = useRef(null);
  const [camOpen, setCamOpen] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Image must be under 5 MB");
    const rd = new FileReader();
    rd.onload = () => onCapture(rd.result);
    rd.readAsDataURL(f);
    e.target.value = "";
  };

  return (
    <div className="flex-1">
      <p className="label-overline mb-2 flex items-center gap-1">
        <Icon size={12} /> {label} {image && <CheckCircleIcon size={12} className="text-[hsl(var(--success))]" />}
      </p>
      <div className={`aspect-[4/3] rounded-lg border-2 ${image ? "border-solid border-[hsl(var(--success))]/40" : "border-dashed border-border"} bg-muted/30 relative overflow-hidden group`}>
        {image ? (
          <img src={image} alt={label} className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground text-xs gap-1 p-4 text-center">
            <Icon size={28} weight="duotone" className="opacity-40" />
            <p>No {label.toLowerCase()} yet</p>
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-2">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testId}-file-input`} />
        <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => setCamOpen(true)} data-testid={`${testId}-camera-btn`}>
          <CameraIcon size={12} className="mr-1" /> Camera
        </Button>
        <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => fileRef.current?.click()} data-testid={`${testId}-upload-btn`}>
          <UploadSimpleIcon size={12} className="mr-1" /> Upload
        </Button>
      </div>
      <CameraCapture
        open={camOpen}
        onClose={() => setCamOpen(false)}
        onCapture={onCapture}
        title={`Capture ${label}`}
      />
    </div>
  );
}

export default function GuestKyc({ guest, onSaved }) {
  const [photo, setPhoto] = useState(guest.photo || "");
  const [idImage, setIdImage] = useState(guest.id_card_image || "");
  const [extraImages, setExtraImages] = useState(guest.id_card_images || []);
  const [idType, setIdType] = useState(guest.id_type || "aadhar");
  const [idNumber, setIdNumber] = useState(guest.id_number || "");
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null); // {src, label}
  const extraFileRef = useRef(null);

  useEffect(() => {
    setPhoto(guest.photo || "");
    setIdImage(guest.id_card_image || "");
    setExtraImages(guest.id_card_images || []);
    setIdType(guest.id_type || "aadhar");
    setIdNumber(guest.id_number || "");
  }, [guest.id]);

  const savePhoto = async (dataUrl) => {
    setPhoto(dataUrl);
    try {
      await api.post(`/guests/${guest.id}/photo`, { image: dataUrl });
      toast.success("Photo saved");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const saveIdCard = async (dataUrl) => {
    setIdImage(dataUrl);
    try {
      await api.post(`/guests/${guest.id}/id-card`, { image: dataUrl, id_type: idType, id_number: idNumber });
      toast.success("ID card saved");
      onSaved?.();
      // Auto-trigger OCR in the background
      runOcr(dataUrl, /* silent */ true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const [ocrBusy, setOcrBusy] = useState(false);
  const runOcr = async (dataUrl, silent = false) => {
    const img = dataUrl || idImage;
    if (!img) return toast.error("Upload an ID card image first");
    setOcrBusy(true);
    try {
      const r = await api.post(`/guests/${guest.id}/ocr-id`, { image: img });
      if (r.data.id_type) setIdType(r.data.id_type);
      if (r.data.id_number) setIdNumber(r.data.id_number);
      if (!silent) toast.success(`Auto-filled: ${r.data.id_type} · ${r.data.id_number || "no number detected"}`);
      else if (r.data.id_number) toast.success(`ID auto-detected: ${r.data.id_type}`);
      onSaved?.();
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "OCR failed. You can type the number manually.");
    } finally { setOcrBusy(false); }
  };

  const saveIdMeta = async () => {
    setSaving(true);
    try {
      await api.patch(`/guests/${guest.id}`, { id_type: idType, id_number: idNumber });
      toast.success("ID details saved");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const [extraCamOpen, setExtraCamOpen] = useState(false);
  const addExtraImage = async (dataUrl) => {
    try {
      const r = await api.post(`/guests/${guest.id}/id-card-extra`, { image: dataUrl });
      setExtraImages((prev) => [...prev, dataUrl]);
      toast.success(`Extra ID image added (${r.data.count} total)`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
  };

  const onExtraFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB"); e.target.value = ""; return; }
    const rd = new FileReader();
    rd.onload = () => { addExtraImage(rd.result); };
    rd.readAsDataURL(f);
    e.target.value = "";
  };

  const removeExtra = async (idx) => {
    if (!window.confirm("Remove this ID image?")) return;
    try {
      await api.delete(`/guests/${guest.id}/id-card-extra/${idx}`);
      setExtraImages((prev) => prev.filter((_, i) => i !== idx));
      toast.success("Removed");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const complete = !!photo && !!idImage;

  return (
    <div className="rounded-lg border border-border p-4 bg-card" data-testid="guest-kyc">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium">Mandatory KYC</p>
        {complete ? (
          <span className="text-xs flex items-center gap-1 text-[hsl(var(--success))]"><CheckCircleIcon size={12} /> Ready for check-in</span>
        ) : (
          <span className="text-xs flex items-center gap-1 text-[hsl(var(--warning))]"><WarningIcon size={12} /> Check-in blocked until complete</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <KycSlot label="Guest photo" image={photo} onCapture={savePhoto} testId="kyc-photo" icon={UserCircleIcon} />
        <KycSlot label="ID card image" image={idImage} onCapture={saveIdCard} testId="kyc-id" icon={IdentificationCardIcon} />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <Label className="text-xs">ID type</Label>
          <Select value={idType} onValueChange={setIdType}>
            <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="aadhar">Aadhaar</SelectItem>
              <SelectItem value="passport">Passport</SelectItem>
              <SelectItem value="driving_license">Driving license</SelectItem>
              <SelectItem value="voter_id">Voter ID</SelectItem>
              <SelectItem value="pan">PAN</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs flex items-center justify-between">
            <span>ID number</span>
            {idImage && (
              <button
                type="button"
                onClick={() => runOcr()}
                disabled={ocrBusy}
                className="text-primary hover:underline text-[10px] flex items-center gap-1 disabled:opacity-50"
                data-testid="kyc-ocr-btn"
              >
                <MagicWandIcon size={10} />{ocrBusy ? "Reading…" : "Auto-fill from image"}
              </button>
            )}
          </Label>
          <Input value={idNumber} onChange={(e) => setIdNumber(e.target.value)} onBlur={saveIdMeta} placeholder="XXXX-XXXX-XXXX" className="h-9 mt-1" data-testid="kyc-id-number" />
        </div>
      </div>

      {/* Additional ID card images (back-side, second doc, address proof etc.) */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-sm font-medium flex items-center gap-1">
              Additional ID photos
              <span className="text-xs text-muted-foreground font-normal">({extraImages.length}/8)</span>
            </p>
            <p className="text-[10px] text-muted-foreground">Back side, second document, or address proof — up to 8 images.</p>
          </div>
          <div className="flex gap-2">
            <input ref={extraFileRef} type="file" accept="image/*" className="hidden" onChange={onExtraFile} data-testid="kyc-extra-file-input" />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={extraImages.length >= 8}
              onClick={() => setExtraCamOpen(true)}
              data-testid="kyc-extra-camera-btn"
            >
              <CameraIcon size={12} className="mr-1" /> Camera
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={extraImages.length >= 8}
              onClick={() => extraFileRef.current?.click()}
              data-testid="kyc-extra-upload-btn"
            >
              <PlusIcon size={12} className="mr-1" /> Add image
            </Button>
          </div>
        </div>

        {extraImages.length > 0 && (
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mt-3" data-testid="kyc-extra-grid">
            {extraImages.map((img, idx) => (
              <div key={idx} className="relative group aspect-[4/3] rounded-md overflow-hidden border border-border bg-muted">
                <img
                  src={img}
                  alt={`ID extra ${idx + 1}`}
                  className="w-full h-full object-cover cursor-zoom-in"
                  onClick={() => setLightbox({ src: img, label: `Additional ID · #${idx + 1}` })}
                  data-testid={`kyc-extra-thumb-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => removeExtra(idx)}
                  className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove"
                  data-testid={`kyc-extra-remove-${idx}`}
                >
                  <TrashIcon size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <CameraCapture
        open={extraCamOpen}
        onClose={() => setExtraCamOpen(false)}
        onCapture={addExtraImage}
        title="Capture additional ID image"
      />

      {lightbox && (
        <Dialog open onOpenChange={(v) => !v && setLightbox(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{lightbox.label}</DialogTitle></DialogHeader>
            <img src={lightbox.src} alt={lightbox.label} className="w-full max-h-[70vh] object-contain" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
